import { effect, inject, mergeApplicationConfig, provideAppInitializer } from '@angular/core';
import {
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  setAnalyticsCollectionEnabled,
  setConsent,
  UserTrackingService,
} from '@angular/fire/analytics';
import { FirebaseApp } from '@angular/fire/app';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import { bootstrapApplication } from '@angular/platform-browser';
import { register as registerSwiper } from 'swiper/element';

import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { CookieConsentService, IS_ANALYTICS_CONFIGURED } from './app/shared/cookie-consent.service';
import { PwaService } from './app/shared/pwa.service';
import { environment } from './environments/environment';

registerSwiper();

// Browser-only providers, the app shell render uses app.config.server.ts instead
const browserConfig = mergeApplicationConfig(appConfig, {
  providers: [
    provideAuth(() => {
      // Same persistence as getAuth(), but without the popup/redirect resolver, which the app doesn't use.
      // Storage isn't provided here so its SDK stays out of the initial bundle, see injectStorage().
      const auth = initializeAuth(inject(FirebaseApp), {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
      });
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      }
      return auth;
    }),
    // getAnalytics() throws without the analytics config (projectId, appId, measurementId)
    ...(IS_ANALYTICS_CONFIGURED
      ? [
          provideAnalytics(() => {
            const analytics = getAnalytics();
            const consentService = inject(CookieConsentService);
            setAnalyticsCollectionEnabled(analytics, consentService.hasAccepted());
            effect(() => {
              const preference = consentService.preference();
              if (preference === 'accepted') {
                setConsent({
                  analytics_storage: 'granted',
                  ad_storage: 'denied',
                  ad_user_data: 'denied',
                  ad_personalization: 'denied',
                });
                setAnalyticsCollectionEnabled(analytics, true);
              } else if (preference === 'denied') {
                setConsent({
                  analytics_storage: 'denied',
                  ad_storage: 'denied',
                  ad_user_data: 'denied',
                  ad_personalization: 'denied',
                });
                setAnalyticsCollectionEnabled(analytics, false);
                consentService.clearAnalyticsCookies();
              }
            });
            return analytics;
          }),
          ScreenTrackingService,
          UserTrackingService,
        ]
      : []),
    provideAppInitializer(() => {
      inject(PwaService).init();
      if (IS_ANALYTICS_CONFIGURED) {
        inject(ScreenTrackingService);
        inject(UserTrackingService);
      }
    }),
  ],
});

bootstrapApplication(AppComponent, browserConfig).catch((err) => console.error(err));
